import React, { useState, useRef, useCallback } from 'react';
import { SECTIONS } from '../data/sections';
import { ROLES, PHASES } from '../data/roles';
import { PROCESS_META, PHASE_TREE } from '../data/processMeta';
import { getSectionProgress, getPctColor } from '../utils/progress';
import { exportTreeSVG } from '../utils/exportUtils';
import './ProcessTree.css';

const STATUS_COLOR = { Done:'#375623', 'In Progress':'#854F0B', Blocked:'#A32D2D' };
const STATUS_BG    = { Done:'#E2EFDA', 'In Progress':'#FFF2CC', Blocked:'#FCEBEB' };

function getSectionStatus(progress, sk) {
  const sp = getSectionProgress(progress, sk);
  if (sp.pct === 100) return 'Done';
  if (sp.blocked > 0) return 'Blocked';
  if (sp.done > 0 || sp.inProg > 0) return 'In Progress';
  return 'Not Started';
}

// ── Vertical Tree View ────────────────────────────────────────────
function VerticalTree({ roleSections, progress, onSection, selected }) {
  const relevantPhases = PHASE_TREE.filter(ph =>
    ph.sections.some(s => roleSections.includes(s))
  );

  return (
    <div className="vtree">
      {relevantPhases.map((ph, pi) => {
        const rel = ph.sections.filter(s => roleSections.includes(s));
        return (
          <div key={ph.id} className="vtree-phase">
            <div className="vtree-phase-label" style={{ borderColor: ph.color, color: ph.color }}>
              <span className="vtree-phase-code" style={{ background: ph.color }}>{ph.id}</span>
              {ph.label}
            </div>
            {pi < relevantPhases.length - 1 && (
              <div className="vtree-connector" style={{ borderColor: ph.color + '44' }} />
            )}
            <div className="vtree-sections">
              {rel.map((sk, si) => {
                const meta   = PROCESS_META[sk] || {};
                const status = getSectionStatus(progress, sk);
                const sp     = getSectionProgress(progress, sk);
                const isParallel = meta.parallel?.some(p => rel.includes(p));
                return (
                  <div key={sk} className={`vtree-sec-wrap${isParallel ? ' vtree-sec-parallel' : ''}`}>
                    <SectionNode
                      sk={sk} meta={meta} status={status} sp={sp}
                      color={ph.color} onClick={() => onSection(sk)}
                      roleSections={roleSections} selected={selected === sk}
                    />
                    {si < rel.length - 1 && !isParallel && (
                      <div className="vtree-sec-arrow">↓</div>
                    )}
                    {isParallel && <div className="vtree-parallel-badge">⟷ Parallel</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Swimlane View ────────────────────────────────────────────────
function SwimlaneView({ roleSections, progress, onSection, selected }) {
  const relevantPhases = PHASE_TREE.filter(ph =>
    ph.sections.some(s => roleSections.includes(s))
  );

  // Group sections by role owner
  const ROLE_LANES = [
    { id: 'PM',     label: 'Project Manager',       color: '#375623' },
    { id: 'FC',     label: 'Functional Consultant',  color: '#7B3F00' },
    { id: 'DEV',    label: 'Developer',               color: '#1B4F72' },
    { id: 'DEVOPS', label: 'DevOps Engineer',          color: '#4A235A' },
    { id: 'TESTER', label: 'Tester',                   color: '#922B21' },
  ];

  const SECTION_LANES = {
    '0.1':'PM','0.2':'PM','0.3':'PM',
    '1.1':'FC','1.2':'FC','1.3':'FC',
    '2.1':'FC','2.2':'FC',
    '3.1':'FC','3.2':'DEV','3.3':'DEV','3.4':'PM',
    '4.1':'TESTER','4.2':'TESTER',
    '5.1':'FC','5.2':'FC','5.3':'FC',
    '6.1':'DEVOPS','6.2':'PM',
    '7.1':'PM','7.2':'FC','7.3':'PM',
    'X.1':'PM','X.2':'PM','X.3':'FC',
  };

  return (
    <div className="swimlane">
      <div className="swimlane-header">
        <div className="swimlane-phase-col" />
        {ROLE_LANES.map(lane => (
          <div key={lane.id} className="swimlane-lane-header" style={{ background: lane.color }}>
            {lane.label}
          </div>
        ))}
      </div>
      {relevantPhases.map(ph => {
        const rel = ph.sections.filter(s => roleSections.includes(s));
        return (
          <div key={ph.id} className="swimlane-row">
            <div className="swimlane-phase-label" style={{ background: ph.color }}>
              <span>{ph.id}</span>
              <small>{ph.label}</small>
            </div>
            {ROLE_LANES.map(lane => {
              const laneSecs = rel.filter(sk => SECTION_LANES[sk] === lane.id);
              return (
                <div key={lane.id} className="swimlane-cell">
                  {laneSecs.map(sk => {
                    const status = getSectionStatus(progress, sk);
                    const sp     = getSectionProgress(progress, sk);
                    const meta   = PROCESS_META[sk] || {};
                    const isParallel = meta.parallel?.some(p => rel.includes(p));
                    return (
                      <div
                        key={sk}
                        className={`swimlane-node${isParallel ? ' swimlane-node--parallel' : ''}${sk === selected ? ' swimlane-node--selected' : ''}`}
                        style={{
                          borderColor: STATUS_COLOR[status] || '#CCCCCC',
                          background: STATUS_BG[status] || '#F8F8F8',
                        }}
                        onClick={() => onSection(sk)}
                        title={`${sk} — ${SECTIONS[sk]?.title}`}
                      >
                        <div className="swimlane-node-code" style={{ background: ph.color }}>{sk}</div>
                        <div className="swimlane-node-title">{SECTIONS[sk]?.title}</div>
                        <div className="swimlane-node-pct">{sp.pct}%</div>
                        {isParallel && <div className="swimlane-parallel-dot" title="Runs in parallel" />}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── Mind Map View ────────────────────────────────────────────────
function MindMapView({ roleSections, progress, onSection, roleColor, selected }) {
  const W = 900, H = 700;
  const cx = W / 2, cy = H / 2;
  const relevantPhases = PHASE_TREE.filter(ph =>
    ph.sections.some(s => roleSections.includes(s))
  );
  const n = relevantPhases.length;

  return (
    <div className="mindmap-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="mindmap-svg">
        {/* Center node */}
        <circle cx={cx} cy={cy} r={42} fill={roleColor} />
        <text x={cx} y={cy-6} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="600">SDLC</text>
        <text x={cx} y={cy+8} textAnchor="middle" fill="#BDD7EE" fontSize="9">Process</text>

        {relevantPhases.map((ph, i) => {
          const angle   = (2 * Math.PI * i / n) - Math.PI / 2;
          const dist    = 220;
          const px      = cx + dist * Math.cos(angle);
          const py      = cy + dist * Math.sin(angle);
          const rel     = ph.sections.filter(s => roleSections.includes(s));

          return (
            <g key={ph.id}>
              {/* Line from center to phase */}
              <line x1={cx} y1={cy} x2={px} y2={py}
                stroke={ph.color} strokeWidth="2" strokeDasharray="4 2" opacity="0.6" />

              {/* Phase bubble */}
              <ellipse cx={px} cy={py} rx={46} ry={22} fill={ph.color} />
              <text x={px} y={py-3} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="600">{ph.id}</text>
              <text x={px} y={py+8} textAnchor="middle" fill="#BDD7EE" fontSize="8">{ph.label.split(' ')[0]}</text>

              {/* Section nodes around phase */}
              {rel.map((sk, si) => {
                const secAngle = angle + (si - (rel.length-1)/2) * 0.45;
                const secDist  = dist + 95;
                const sx       = cx + secDist * Math.cos(secAngle);
                const sy       = cy + secDist * Math.sin(secAngle);
                const status   = getSectionStatus(progress, sk);
                const sp       = getSectionProgress(progress, sk);
                const meta     = PROCESS_META[sk] || {};
                const isParallel = meta.parallel?.some(p => rel.includes(p));
                const fill     = STATUS_BG[status] || '#F8F8F8';
                const stroke   = STATUS_COLOR[status] || ph.color;

                return (
                  <g key={sk} onClick={() => onSection(sk)} style={{ cursor: 'pointer' }}>
                    <line x1={px} y1={py} x2={sx} y2={sy}
                      stroke={stroke} strokeWidth="1.5" opacity="0.5" />
                    <rect x={sx-28} y={sy-14} width={56} height={28}
                      rx={6} fill={fill} stroke={sk === selected ? '#1F4E79' : stroke} strokeWidth={sk === selected ? '3' : '1.5'} />
                    <text x={sx} y={sy-3} textAnchor="middle" fontSize="8" fontWeight="700" fill={stroke}>{sk}</text>
                    <text x={sx} y={sy+7} textAnchor="middle" fontSize="7" fill="#555">{sp.pct}%</text>
                    {isParallel && (
                      <circle cx={sx+24} cy={sy-10} r={4} fill="#FFC000" stroke="#fff" strokeWidth="1" />
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="mindmap-legend">
        {[['Done','#E2EFDA','#375623'],['In Progress','#FFF2CC','#854F0B'],
          ['Blocked','#FCEBEB','#A32D2D'],['Not Started','#F0F0F0','#999']].map(([l,bg,c])=>(
          <div key={l} className="mindmap-legend-item">
            <span style={{background:bg,border:`2px solid ${c}`,width:12,height:12,borderRadius:3,display:'inline-block'}}/>
            <span style={{color:c,fontSize:11}}>{l}</span>
          </div>
        ))}
        <div className="mindmap-legend-item">
          <span style={{background:'#FFC000',borderRadius:'50%',width:10,height:10,display:'inline-block'}}/>
          <span style={{fontSize:11,color:'#666'}}>Parallel task</span>
        </div>
      </div>
    </div>
  );
}

// ── Section Node (shared) ─────────────────────────────────────────
function SectionNode({ sk, meta, status, sp, color, onClick, roleSections, selected }) {
  const prereqMissed = (meta.prerequisites || []).filter(p =>
    roleSections.includes(p) && getSectionStatus({}, p) !== 'Done'
  );

  return (
    <div
      className="tree-sec-node"
      style={{
        borderColor: selected ? '#1F4E79' : (STATUS_COLOR[status] || color),
        background:  STATUS_BG[status] || '#F8F8F8',
        boxShadow: selected ? '0 0 0 3px rgba(31,78,121,0.25)' : 'none',
      }}
      onClick={onClick}
    >
      <div className="tree-node-header">
        <span className="tree-node-code" style={{ background: color }}>{sk}</span>
        <span className="tree-node-title">{SECTIONS[sk]?.title}</span>
        {meta.critical && <span className="tree-critical-badge">Critical</span>}
      </div>
      <div className="tree-node-footer">
        <div className="tree-node-pbar">
          <div style={{ width: `${sp.pct}%`, background: STATUS_COLOR[status] || color,
            height: '100%', borderRadius: 2, transition: 'width 0.3s' }} />
        </div>
        <span className="tree-node-pct" style={{ color: STATUS_COLOR[status] || color }}>
          {sp.pct}%
        </span>
      </div>
      {(meta.prerequisites?.length > 0) && (
        <div className="tree-prereq-line">
          <i className="ti ti-arrow-back-up" style={{fontSize:10}} />
          Requires: {meta.prerequisites.join(', ')}
        </div>
      )}
      {(meta.parallel?.filter(p => roleSections.includes(p)).length > 0) && (
        <div className="tree-parallel-line">
          <i className="ti ti-arrows-right-left" style={{fontSize:10}} />
          Parallel: {meta.parallel.filter(p => roleSections.includes(p)).join(', ')}
        </div>
      )}
    </div>
  );
}

// ── Main ProcessTree Component ────────────────────────────────────
export default function ProcessTree({ state, onOpenSection, selectedSection, onClose }) {
  const [view, setView] = useState('vertical');
  const svgRef = useRef(null);
  const role = state.role ? ROLES[state.role] : null;
  if (!role) return null;

  const roleSections  = role.sections;
  const roleColor     = { COO:'#1F4E79',PM:'#375623',FC:'#7B3F00',DEV:'#1B4F72',DEVOPS:'#4A235A',TESTER:'#922B21' }[state.role] || '#1F4E79';

  const handleExport = () => {
    const svgEl = document.querySelector('.mindmap-svg') || document.querySelector('.vtree');
    exportTreeSVG(svgEl, state.role);
  };

  return (
    <div className="process-tree-panel">
      <div className="tree-toolbar">
        <div className="tree-title">
          <i className="ti ti-topology-star-3" style={{fontSize:16,color:roleColor}} aria-hidden="true" />
          Process Tree — {role.label}
        </div>
        <div className="tree-view-tabs">
          {[['vertical','ti-layout-list','Vertical'],
            ['mindmap','ti-affiliate','Mind Map'],
            ['swimlane','ti-table','Swimlane']].map(([v,icon,label])=>(
            <button
              key={v}
              className={`tree-view-tab${view===v?' active':''}`}
              style={view===v?{borderColor:roleColor,color:roleColor}:{}}
              onClick={()=>setView(v)}
            >
              <i className={`ti ${icon}`} aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </div>
        <button className="btn btn-sm" onClick={handleExport} title="Export tree as SVG">
          <i className="ti ti-download" aria-hidden="true" />
          Export
        </button>
        {onClose && (
          <button className="btn btn-sm" onClick={onClose} title="Close tree">
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="tree-legend">
        {[['Done','#E2EFDA','#375623'],['In Progress','#FFF2CC','#854F0B'],
          ['Blocked','#FCEBEB','#A32D2D'],['Not Started','#F0F0F0','#888']].map(([l,bg,c])=>(
          <div key={l} className="tree-legend-item">
            <span className="tree-legend-swatch" style={{background:bg,borderColor:c}} />
            <span style={{color:c}}>{l}</span>
          </div>
        ))}
        <div className="tree-legend-item">
          <span className="tree-legend-swatch" style={{background:'#FFF2CC',borderColor:'#FFC000',border:'2px dashed #FFC000'}} />
          <span style={{color:'#666'}}>Parallel tasks</span>
        </div>
        <div className="tree-legend-item">
          <span className="tree-legend-badge">Critical</span>
          <span style={{color:'#666'}}>Critical path</span>
        </div>
      </div>

      {/* Tree view */}
      <div className="tree-content" ref={svgRef}>
        {view === 'vertical'  && <VerticalTree  roleSections={roleSections} progress={state.progress} onSection={onOpenSection} selected={selectedSection} />}
        {view === 'mindmap'   && <MindMapView   roleSections={roleSections} progress={state.progress} onSection={onOpenSection} roleColor={roleColor} selected={selectedSection} />}
        {view === 'swimlane'  && <SwimlaneView  roleSections={roleSections} progress={state.progress} onSection={onOpenSection} selected={selectedSection} />}
      </div>
    </div>
  );
}
